import axios from 'axios';
import { featuredSlug, hasFeaturedSessionPrefix, isFeaturedSessionId } from '../utils/featuredSession';

// In production (Railway), frontend is served from same domain as backend
// In development, REACT_APP_BACKEND_URL should point to http://localhost:8001
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// F4 Phase 1 invariant: these client budgets MUST stay ABOVE the server-side
// interrogation deadlines (RUNTIME_INTERROGATION_CONDUCT_DEADLINE_SECONDS=45,
// RUNTIME_INTERROGATION_INIT_DEADLINE_SECONDS=100 in backend/config.py) so
// the server always answers (best-effort ship or structured 503) before the
// client aborts. Bump the server values first if these ever change.
const INTERROGATION_INIT_TIMEOUT_MS = 120000;
const INTERROGATION_CONDUCT_TIMEOUT_MS = 60000;

// Create axios instance with retry logic for network errors
const axiosInstance = axios.create({
  baseURL: API,
});

// Store for the getToken function from Clerk
let getTokenFn = null;

// Track if case generation is in progress (prevents auth-session-expired during long requests)
let caseGenerationInProgress = false;

// Set the getToken function (called from useAuthSetup hook)
export const setAuthTokenGetter = (getToken) => {
  getTokenFn = getToken;
};

// Track case generation state (used to suppress auth events during generation)
export const setCaseGenerationInProgress = (inProgress) => {
  caseGenerationInProgress = inProgress;
  // Also set a window flag for debugging
  if (typeof window !== 'undefined') {
    window.__caseGenerationInProgress = inProgress;
  }
};

export const isCaseGenerationInProgress = () => caseGenerationInProgress;

/**
 * Validates auth token before long-running operations and RETURNS the token.
 * This prevents race conditions where Clerk refreshes between validation and request.
 * Throws an error with isAuthError and isPreflightFailure flags if validation fails.
 * @param {string} context - Log context (e.g., 'gameAPI', 'gameAPI SSE')
 * @returns {Promise<string>} The validated token to use for the request
 */
const validatePreflightToken = async (context = 'gameAPI') => {
  if (getTokenFn) {
    try {
      const preflightToken = await getTokenFn();
      if (!preflightToken) {
        console.error(`[${context}] Pre-flight token check failed: no token available`);
        const error = new Error('Session expired before case generation started');
        error.isAuthError = true;
        error.isPreflightFailure = true;
        throw error;
      }
      console.log(`[${context}] Pre-flight token check passed`);
      return preflightToken; // Return the token for direct use
    } catch (preflightError) {
      if (preflightError.isPreflightFailure) {
        throw preflightError;
      }
      console.error(`[${context}] Pre-flight token check error:`, preflightError.message);
      const error = new Error('Failed to validate session before case generation');
      error.isAuthError = true;
      error.isPreflightFailure = true;
      throw error;
    }
  } else {
    console.error(`[${context}] No token getter available - user not signed in`);
    const error = new Error('Not signed in');
    error.isAuthError = true;
    error.isPreflightFailure = true;
    throw error;
  }
};

// Add request interceptor to automatically attach auth token
// Includes retry logic for cases where Clerk is mid-refresh
axiosInstance.interceptors.request.use(async (config) => {
  // Skip auth for public endpoints (relative paths only, baseURL is already /api)
  const publicEndpoints = ['/health'];
  const isPublicEndpoint = publicEndpoints.some(ep => config.url?.includes(ep));

  if (getTokenFn && !isPublicEndpoint) {
    // Skip if caller already provided Authorization header (pre-validated token)
    // This preserves tokens from validatePreflightToken() in startGame/startGameAsync
    if (config.headers.Authorization) {
      return config;
    }

    // Try to get token with retry (handles Clerk mid-refresh race condition)
    let token = null;
    let lastError = null;

    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        token = await getTokenFn();
        if (token) {
          break; // Success, exit retry loop
        }
        // Token is null - Clerk might be refreshing, wait briefly
        if (attempt < 2) {
          console.log(`[gameAPI] Token null on attempt ${attempt + 1}, retrying in ${100 * (attempt + 1)}ms...`);
          await new Promise(resolve => setTimeout(resolve, 100 * (attempt + 1)));
        }
      } catch (error) {
        lastError = error;
        console.warn(`[gameAPI] Token fetch failed (attempt ${attempt + 1}):`, error.message);
        if (attempt < 2) {
          await new Promise(resolve => setTimeout(resolve, 100 * (attempt + 1)));
        }
      }
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      // No token after retries - log warning but proceed
      // The 401 will be handled by the response interceptor
      console.warn('[gameAPI] No auth token available after retries', lastError?.message || 'token was null');
    }
  } else if (!getTokenFn && !isPublicEndpoint) {
    // getTokenFn not set - user may have been signed out
    console.warn('[gameAPI] No auth token getter available - user may be signed out');
  }

  return config;
}, (error) => {
  return Promise.reject(error);
});

// Add response interceptor to handle connection and auth errors
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    // Enhance error information for better handling
    if (error.code === 'ECONNABORTED' && error.message.includes('timeout')) {
      error.isTimeout = true;
    }
    // Detect network errors - various error codes/messages for connection issues
    const errorCode = error.code || '';
    const errorMsg = (error.message || '').toLowerCase();
    if (error.message === 'Network Error' ||
        errorCode === 'ERR_NETWORK' ||
        errorCode === 'ERR_CONNECTION_CLOSED' ||
        errorCode === 'ERR_CONNECTION_RESET' ||
        errorCode === 'ECONNRESET' ||
        errorCode === 'ECONNABORTED' ||
        errorMsg.includes('network error') ||
        errorMsg.includes('failed to fetch') ||
        errorMsg.includes('connection') ||
        !error.response) { // No response usually means network failure
      error.isNetworkError = true;
    }

    // Annotate structured 503 retryable errors (matches backend contract:
    // { error, message, retryable: true } in error.response.data.detail).
    // The retry itself is user-driven via a Try Again button — we do NOT
    // auto-retry here.
    if (
      error.response?.status === 503 &&
      error.response?.data?.detail?.retryable === true
    ) {
      error.isRetryable = true;
    }

    // Handle authentication errors (session expired, invalid token)
    // IMPORTANT: Suppress auth events during case generation to prevent kicking user out
    // Case generation takes 4-12 minutes, and stray 401s from duplicate requests shouldn't interrupt
    if (error.response?.status === 401) {
      error.isAuthError = true;

      // Only dispatch session-expired if NOT during case generation
      if (!caseGenerationInProgress && !window.__authSessionExpiredDispatched) {
        window.__authSessionExpiredDispatched = true;

        // Dispatch custom event for global auth error handling
        window.dispatchEvent(new CustomEvent('auth-session-expired', {
          detail: {
            message: 'Your session has expired. Please sign in again.',
            originalError: error
          }
        }));

        // Clear flag after auth flow completes (allow new events after redirect)
        setTimeout(() => {
          window.__authSessionExpiredDispatched = false;
        }, 5000);
      } else if (caseGenerationInProgress) {
        console.warn('[gameAPI] Suppressed auth-session-expired during case generation');
      }
    }

    return Promise.reject(error);
  }
);

// ===== FEATURED-CASE URL SWITCH =====
// A featured case is played through the same game screen and the same gameAPI
// methods as a generated one; only the URL prefix differs. The compat routes
// (`backend/routes/featured_cases.py`, "PRO-GEN CONTRACT LAYER") mirror each
// pro-gen sub-path one-for-one, so every call below names its sub-path once and
// `gameUrl` decides which surface it lands on. Components must never branch on
// "is this featured" to pick a URL.
const featuredCompatUrl = (slug, subPath) => (
  `/static-cases/${encodeURIComponent(slug)}/compat/${subPath}`
);

/**
 * Build a game-screen URL for either surface.
 *
 * @param {string} sessionId - store session id (UUID, or `featured:{slug}`).
 * @param {string} subPath - the sub-path shared by both surfaces.
 * @param {string} [progenPath] - the pro-gen path when it is NOT
 *   `/game/{sessionId}/{subPath}` (e.g. `/game/action`, `/game/load/{id}`).
 * @returns {string}
 */
const gameUrl = (sessionId, subPath, progenPath = null) => {
  const slug = featuredSlug(sessionId);
  if (slug) {
    return featuredCompatUrl(slug, subPath);
  }
  // A bare `featured:` is neither surface: interpolating it into the pro-gen
  // path would ship a malformed URL to the generated-case routes.
  if (hasFeaturedSessionPrefix(sessionId)) {
    const error = new Error('Malformed featured session id: no slug');
    error.isMalformedSessionId = true;
    throw error;
  }
  // Pro-gen interpolation is left byte-identical to what it has always been.
  return progenPath ?? `/game/${sessionId}/${subPath}`;
};

/**
 * Hard stop for the four pro-gen-only surfaces that have no compat route:
 * case generation (start/start-async/generation-jobs), runtime image
 * regeneration, the legacy combined news call and news history. A featured
 * session must never reach them; call sites hide the control, and this is the
 * backstop if one slips through.
 */
const rejectFeaturedSession = (sessionId, operation) => {
  if (isFeaturedSessionId(sessionId)) {
    const error = new Error(`${operation} is not available for featured cases`);
    error.isFeaturedUnsupported = true;
    throw error;
  }
};

export { featuredSlug, isFeaturedSessionId };

// Track active start-game request to prevent duplicates
let activeStartGameController = null;

const buildQueryString = (params = {}) => {
  const searchParams = new URLSearchParams();
  const shouldSkipValue = (value) => (
    value === undefined || value === null || value === '' || value === 'all'
  );

  Object.entries(params).forEach(([key, value]) => {
    if (shouldSkipValue(value)) {
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (!shouldSkipValue(item)) {
          searchParams.append(key, String(item));
        }
      });
      return;
    }

    searchParams.append(key, String(value));
  });

  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
};

const withCompatibilityParams = (filters = {}, aliases = {}) => {
  const params = { ...filters };

  Object.entries(aliases).forEach(([sourceKey, targetKey]) => {
    if (
      params[sourceKey] !== undefined &&
      params[sourceKey] !== null &&
      params[sourceKey] !== '' &&
      (params[targetKey] === undefined || params[targetKey] === null || params[targetKey] === '')
    ) {
      params[targetKey] = params[sourceKey];
    }
  });

  return params;
};

export const gameAPI = {
  // Start a new game (long timeout for case generation)
  // Uses AbortController to prevent duplicate requests
  async startGame(theme, difficulty) {
    // If there's already an active request, abort it (shouldn't happen, but safety)
    if (activeStartGameController) {
      console.warn('[gameAPI] Aborting previous start-game request');
      activeStartGameController.abort();
    }

    // Pre-flight token validation - fail fast before starting long generation
    // This catches cases where the session expired between DifficultySelection and here
    // IMPORTANT: We capture and use this token directly to avoid Clerk refresh race conditions
    const validatedToken = await validatePreflightToken('gameAPI');

    // Create new controller for this request
    const controller = new AbortController();
    activeStartGameController = controller;

    // Mark generation as in progress (suppresses auth-session-expired events)
    setCaseGenerationInProgress(true);

    try {
      const response = await axiosInstance.post('/game/start', {
        theme,
        difficulty
      }, {
        timeout: 600000, // 10 minutes
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
        signal: controller.signal,
        // Pass the pre-validated token directly to bypass interceptor re-fetch race condition
        headers: {
          Authorization: `Bearer ${validatedToken}`
        }
      });
      return response.data;
    } finally {
      // Only clean up if this is still the active controller (prevents race with cancelStartGame)
      if (activeStartGameController === controller) {
        activeStartGameController = null;
        setCaseGenerationInProgress(false);
      }
    }
  },

  // Start a new game asynchronously and return the generation job metadata
  async startGameAsync(theme, difficulty, { signal } = {}) {
    const throwIfAborted = () => {
      if (signal?.aborted) {
        const error = new Error('Start game request aborted');
        error.name = 'AbortError';
        throw error;
      }
    };

    throwIfAborted();

    // If there's already an active request, abort it (shouldn't happen, but safety)
    if (activeStartGameController) {
      console.warn('[gameAPI] Aborting previous start-game request');
      activeStartGameController.abort();
    }

    // Pre-flight token validation - fail fast before starting generation
    // IMPORTANT: We capture and use this token directly to avoid Clerk refresh race conditions
    const validatedToken = await validatePreflightToken('gameAPI async');
    throwIfAborted();

    // Create new controller for this request
    const controller = new AbortController();
    const handleAbort = () => controller.abort();
    if (signal) {
      signal.addEventListener('abort', handleAbort, { once: true });
      if (signal.aborted) {
        controller.abort();
      }
    }
    activeStartGameController = controller;

    try {
      const response = await axiosInstance.post('/game/start-async', {
        theme,
        difficulty
      }, {
        signal: controller.signal,
        // Pass the pre-validated token directly to bypass interceptor re-fetch race condition
        headers: {
          Authorization: `Bearer ${validatedToken}`
        }
      });
      return response.data;
    } finally {
      if (signal) {
        signal.removeEventListener('abort', handleAbort);
      }
      // Only clean up if this is still the active controller (prevents race with cancelStartGame)
      if (activeStartGameController === controller) {
        activeStartGameController = null;
      }
    }
  },

  async getGenerationJob(jobId, { signal } = {}) {
    const response = await axiosInstance.get(`/game/generation-jobs/${jobId}`, { signal });
    return response.data;
  },

  // Cancel any in-progress start-game request (for cleanup on unmount)
  cancelStartGame() {
    if (activeStartGameController) {
      console.log('[gameAPI] Cancelling start-game request');
      const controller = activeStartGameController;
      activeStartGameController = null;
      setCaseGenerationInProgress(false);
      controller.abort();
    }
  },

  // Process a player action
  // focuses: optional array of focus types for EXAMINE mini-game (e.g., ['physical_traces', 'paper_trail'])
  async processAction(sessionId, action, target = null, focuses = null) {
    const payload = {
      session_id: sessionId,
      action,
      target
    };
    // Add focuses for EXAMINE mini-game if provided
    if (focuses && focuses.length > 0) {
      payload.focuses = focuses;
    }
    const response = await axiosInstance.post(
      gameUrl(sessionId, 'action', '/game/action'),
      payload,
    );
    return response.data;
  },

  // Get examine context hint for a location (pre-selection)
  async getExamineContext(sessionId, locationId, signal) {
    const response = await axiosInstance.get(
      gameUrl(sessionId, `examine-context/${locationId}`),
      { signal },
    );
    return response.data;
  },

  // Make an accusation
  async accuse(sessionId, suspectId, reasoning = null) {
    const response = await axiosInstance.post(gameUrl(sessionId, 'accuse', '/game/accuse'), {
      session_id: sessionId,
      suspect_id: suspectId,
      reasoning
    });
    return response.data;
  },

  // Regenerate an image (generated cases only - featured images are authored)
  async regenerateImage(sessionId, itemType, itemId) {
    rejectFeaturedSession(sessionId, 'Image regeneration');
    const response = await axiosInstance.post('/game/regenerate-image', {
      sessionId,
      imageType: itemType,
      targetId: itemId
    }, {
      timeout: 450000 // covers the server's worst case: 180 s × 2 attempts plus the R2 upload
    });
    return response.data;
  },

  // Get case archive (all previous games)
  async getCaseArchive() {
    const response = await axiosInstance.get('/game/archive');
    return response.data;
  },

  // Delete a game session from archive
  async deleteGameSession(sessionId) {
    const response = await axiosInstance.delete(
      gameUrl(sessionId, `archive/${sessionId}`, `/game/archive/${sessionId}`),
    );
    return response.data;
  },

  // Load a specific game session
  // `options.signal` is optional: existing callers pass nothing and are
  // unaffected; callers that can be superseded (e.g. a route change while a
  // featured case is loading) pass an AbortSignal to drop the stale request.
  async loadGameSession(sessionId, options = {}) {
    const response = await axiosInstance.get(
      gameUrl(sessionId, 'load', `/game/load/${sessionId}`),
      options.signal ? { signal: options.signal } : undefined,
    );
    return response.data;
  },

  // Restart a case in place. Featured-only: generated cases have no server-side
  // restart endpoint, they are abandoned client-side and a new case generated.
  // Returns the same payload shape as `loadGameSession`.
  async restartCase(sessionId) {
    const slug = featuredSlug(sessionId);
    if (!slug) {
      const error = new Error('Restart is only available for featured cases');
      error.isFeaturedOnly = true;
      throw error;
    }
    const response = await axiosInstance.post(featuredCompatUrl(slug, 'restart'), {});
    return response.data;
  },

  // Save evidence board state
  async saveBoardState(sessionId, boardState) {
    const response = await axiosInstance.post(gameUrl(sessionId, 'board'), boardState);
    return response.data;
  },

  // Get evidence board state
  async getBoardState(sessionId) {
    const response = await axiosInstance.get(gameUrl(sessionId, 'board'));
    return response.data;
  },

  // Notebook/Journal API
  async getNotes(sessionId) {
    const response = await axiosInstance.get(gameUrl(sessionId, 'notes'));
    return response.data;
  },

  async addNote(sessionId, content) {
    const response = await axiosInstance.post(gameUrl(sessionId, 'notes'), { content });
    return response.data;
  },

  async updateNote(sessionId, noteId, content) {
    const response = await axiosInstance.put(gameUrl(sessionId, `notes/${noteId}`), { content });
    return response.data;
  },

  async deleteNote(sessionId, noteId) {
    const response = await axiosInstance.delete(gameUrl(sessionId, `notes/${noteId}`));
    return response.data;
  },

  // Get current game score
  async getScore(sessionId) {
    const response = await axiosInstance.get(gameUrl(sessionId, 'score'));
    return response.data;
  },

  // Get case summary (after game ends)
  async getCaseSummary(sessionId) {
    const response = await axiosInstance.get(gameUrl(sessionId, 'summary'), {
      timeout: 120000 // 2 minutes for LLM summary generation
    });
    return response.data;
  },

  // Get tutorial summary (theme-appropriate character fates)
  async getTutorialSummary(sessionId) {
    // V2 format: tutorial_{theme}_{timestamp}
    // Extract theme (handles themes with underscores like "90s_nyc")
    if (sessionId && sessionId.startsWith('tutorial_')) {
      const parts = sessionId.split('_');
      // Get everything between "tutorial_" and the final timestamp
      // e.g., "tutorial_90s_nyc_1234567890" -> ["90s", "nyc"] -> "90s_nyc"
      const theme = parts.slice(1, -1).join('_');

      const response = await axiosInstance.get(`/tutorial-v2/${theme}/summary`, {
        timeout: 5000
      });
      return response.data;
    }

    throw new Error('Invalid tutorial session ID format');
  },

  // Quick check if news should trigger (fast - no generation)
  async checkNewsTrigger(sessionId) {
    const response = await axiosInstance.post(gameUrl(sessionId, 'check-news-trigger'), {}, {
      timeout: 5000 // 5 seconds - this should be fast
    });
    return response.data;
  },

  // Generate news article (slow - includes LLM and image generation)
  async generateNews(sessionId) {
    const response = await axiosInstance.post(gameUrl(sessionId, 'generate-news'), {}, {
      timeout: 150000 // 2.5 minutes for news generation + image (PG-49: server max ~105 s)
    });
    return response.data;
  },

  // Check for news triggers (legacy - does trigger check + generation in one call)
  async checkNews(sessionId) {
    rejectFeaturedSession(sessionId, 'The combined news check');
    const response = await axiosInstance.post(`/game/${sessionId}/check-news`, {}, {
      timeout: 90000 // 1.5 minutes for news generation + image
    });
    return response.data;
  },

  // Get news history
  async getNewsHistory(sessionId) {
    rejectFeaturedSession(sessionId, 'News history');
    const response = await axiosInstance.get(`/game/${sessionId}/news-history`, {
      timeout: 5000 // runs inside the action lock after a failed edition
    });
    return response.data;
  },

  // Initialize interrogation (generates all Round 1 responses)
  async initInterrogation(sessionId, suspectId) {
    const response = await axiosInstance.post(gameUrl(sessionId, 'interrogate/init'), {
      session_id: sessionId,
      suspect_id: suspectId
    }, {
      timeout: INTERROGATION_INIT_TIMEOUT_MS // 2 minutes for generating all 4 approach responses
    });
    return response.data;
  },

  // Conduct interrogation round
  async interrogate(sessionId, suspectId, approach, roundNumber = 1) {
    const response = await axiosInstance.post(gameUrl(sessionId, 'interrogate'), {
      session_id: sessionId,
      suspect_id: suspectId,
      approach,
      round_number: roundNumber
    }, {
      timeout: INTERROGATION_CONDUCT_TIMEOUT_MS // 1 minute for Round 2 generation
    });
    return response.data;
  },

  // Get interrogation status for a suspect
  async getInterrogationStatus(sessionId, suspectId) {
    const response = await axiosInstance.get(
      gameUrl(sessionId, `interrogation-status/${suspectId}`),
    );
    return response.data;
  },

  // === TUTORIAL ENDPOINTS ===

  // Get available tutorial themes
  async getTutorialThemes() {
    const response = await axiosInstance.get('/game/tutorial/themes');
    return response.data;
  },

  // Start tutorial (creates/resets tutorial session)
  async startTutorial(theme = '90s_nyc') {
    const response = await axiosInstance.post('/game/start-tutorial', { theme });
    return response.data;
  },

  // Advance tutorial to next step
  async advanceTutorialStep(sessionId) {
    const response = await axiosInstance.post(`/game/${sessionId}/tutorial/advance`);
    return response.data;
  },

  // Get tutorial status (completion and unlock status)
  async getTutorialStatus() {
    const response = await axiosInstance.get('/user/tutorial-status');
    return response.data;
  },

  // Get current user's app profile
  async getUserProfile() {
    const response = await axiosInstance.get('/user/profile');
    return response.data;
  },

  // Update current user's app display name
  async updateUserProfile(displayName) {
    const response = await axiosInstance.put('/user/profile', {
      display_name: displayName,
    });
    return response.data;
  },

  // Reset tutorial progress to start from beginning
  async resetTutorial() {
    const response = await axiosInstance.post('/game/reset-tutorial');
    return response.data;
  },

  // Initialize new user (creates user_metadata)
  async initializeUser() {
    const response = await axiosInstance.post('/user/initialize');
    return response.data;
  },

  // Admin: Get LLM warning logs
  async getLLMLogs(category = null, sessionId = null, limit = 100, offset = 0) {
    const params = new URLSearchParams();
    if (category) params.append('category', category);
    if (sessionId) params.append('session_id', sessionId);
    params.append('limit', limit);
    params.append('offset', offset);
    const response = await axiosInstance.get(`/admin/llm-logs?${params.toString()}`);
    return response.data;
  },

  // Admin: case review console overview counters
  async getAdminCasesOverview() {
    const response = await axiosInstance.get('/admin/cases/overview');
    return response.data;
  },

  // Admin: reviewed case list with health rollups
  async getAdminCases(filters = {}) {
    const response = await axiosInstance.get(`/admin/cases${buildQueryString(filters)}`);
    return response.data;
  },

  // Admin: durable generation failures
  async getAdminGenerationFailures(filters = {}) {
    const params = withCompatibilityParams(filters, {
      error_type: 'category',
    });
    const response = await axiosInstance.get(`/admin/generation-failures${buildQueryString(params)}`);
    return response.data;
  },

  // Admin: durable generation failure stats
  async getAdminGenerationFailureStats() {
    const response = await axiosInstance.get('/admin/generation-failures/stats');
    return response.data;
  },

  // Admin: generated image audit rows
  async getAdminImages(filters = {}) {
    const params = withCompatibilityParams(filters, {
      subject_type: 'target_type',
    });

    if (params.missing_r2_url === true || params.missing_r2_url === 'true') {
      params.success = false;
    }

    const response = await axiosInstance.get(`/admin/images${buildQueryString(params)}`);
    return response.data;
  },

  // Admin: generated image audit rows for a specific case
  async getCaseImageAudit(caseId, filters = {}) {
    const params = withCompatibilityParams(filters, {
      subject_type: 'target_type',
    });

    if (params.missing_r2_url === true || params.missing_r2_url === 'true') {
      params.success = false;
    }

    const response = await axiosInstance.get(`/admin/case/${encodeURIComponent(caseId)}/image-audit${buildQueryString(params)}`);
    return response.data;
  },

  // Admin: private review metadata for a specific case
  async getCaseReview(caseId) {
    const response = await axiosInstance.get(`/admin/case/${encodeURIComponent(caseId)}/review`);
    return response.data;
  },

  // Admin: update private review metadata for a specific case
  async updateCaseReview(caseId, review) {
    const response = await axiosInstance.put(`/admin/case/${encodeURIComponent(caseId)}/review`, review);
    return response.data;
  },

  // Admin: add a private review note for a specific case
  async addCaseReviewNote(caseId, note) {
    const response = await axiosInstance.post(`/admin/case/${encodeURIComponent(caseId)}/review/notes`, note);
    return response.data;
  },

  // Admin: Clear LLM logs
  async clearLLMLogs(category = null, olderThanDays = null) {
    const params = new URLSearchParams();
    if (category) params.append('category', category);
    if (olderThanDays) params.append('older_than_days', olderThanDays);
    const response = await axiosInstance.delete(`/admin/llm-logs?${params.toString()}`);
    return response.data;
  },

  // Admin: Get case details with generation log
  async getCaseAdminDetails(caseId) {
    const response = await axiosInstance.get(`/admin/case/${caseId}`);
    return response.data;
  },

  // Admin: Run diagnostics on a session
  async getSessionDiagnostics(sessionId) {
    const response = await axiosInstance.get(`/admin/session/${sessionId}/diagnostics`, {
      timeout: 60000  // 60 second timeout - diagnostics can be slow
    });
    return response.data;
  },

  // === CIPHER SYSTEM ENDPOINTS ===

  // Get cipher state and puzzle info (for modal)
  async getCipherState(sessionId) {
    const response = await axiosInstance.get(gameUrl(sessionId, 'cipher'));
    return response.data;
  },

  // Attempt to solve the cipher puzzle
  async attemptCipherSolution(sessionId, solution) {
    const response = await axiosInstance.post(gameUrl(sessionId, 'cipher/attempt'), {
      solution
    });
    return response.data;
  },

  // Admin: Get all users' tutorial status
  async getUsersTutorialStatus() {
    const response = await axiosInstance.get('/admin/users/tutorial-status');
    return response.data;
  },

  // Admin: Toggle user tutorial unlock
  async toggleUserTutorial(userId, unlocked) {
    const response = await axiosInstance.post(`/admin/user/${userId}/toggle-tutorial`, { unlocked });
    return response.data;
  },

  // Case generation quota (for current user)
  async getCaseGenQuota() {
    const response = await axiosInstance.get('/game/case-gen-quota');
    return response.data;
  },

  // Admin: update user daily limit
  async updateUserDailyLimit(userId, dailyLimit, unlimited) {
    const response = await axiosInstance.put(`/admin/user/${userId}/daily-limit`, {
      daily_limit: dailyLimit,
      unlimited,
    });
    return response.data;
  },

  // Admin: update user display name
  async updateUserDisplayName(userId, displayName) {
    const response = await axiosInstance.put(`/admin/user/${userId}/display-name`, {
      display_name: displayName,
    });
    return response.data;
  },

  // Admin: get user's recent cases
  async getUserRecentCases(userId) {
    const response = await axiosInstance.get(`/admin/user/${userId}/recent-cases`);
    return response.data;
  },
};

// Expose gameAPI to window for console debugging (development only)
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  window.gameAPI = gameAPI;
}
